import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AddressData, DavaAddressInfo } from '../../models/dava.model';
import { helvetiaCustomerInfo } from '../../models/helvetia.model';

@Component({
  selector: 'lib-helvetia-customer-info',
  templateUrl: './helvetia-customer-info.component.html',
  styleUrls: ['./helvetia-customer-info.component.scss'],
  standalone: false,
})
export class HelvetiaCustomerInfoComponent {
  @Output() recalculateHelvetiaPricesEvent = new EventEmitter();
  @Output() nextStepEvent = new EventEmitter();
  @Output() cprLookupEvent = new EventEmitter();
  @Output() cvrLookupEvent = new EventEmitter();
  @Output() setCustomerInfoEvent = new EventEmitter<helvetiaCustomerInfo>();
  @Output() cprExtraUserLookupEvent = new EventEmitter();
  @Output() cvrExtraUserLookupEvent = new EventEmitter();
  @Output() newAgeBasedOnCprEvent = new EventEmitter<number>();
  @Output() newAgeBasedOnExtraUserCprEvent = new EventEmitter<number>();

  @Input() cvrLoading: boolean = false;
  @Input() errorInCvrNumber: boolean = false;
  @Input() showAddressOption: boolean = false;
  @Input() errorInCprNumber: boolean = false;
  @Input() cprLoading: boolean = false;

  @Input() extraUserCvrLoading: boolean = false;
  @Input() errorInExtraUserCompanyName: boolean = false;
  @Input() errorInExtraUserCvrNumber: boolean = false;
  @Input() extraUserCprLoading: boolean = false;
  @Input() errorInExtraUserCprNumber: boolean = false;
  @Input() showExtraUserAddressOption: boolean = false;

  @Input() loadinginsuranceInfo: boolean = false;
  @Input() disableCprLookup: boolean = false;
  @Input() textDirectedToCustomer: boolean = false;
  @Input() helvetiaCustomerInfo: helvetiaCustomerInfo = {
    showAsBusinessCustomer: false,
    cprNumber: '',
    cvrNumber: '',
    firstname: '',
    lastname: '',
    companyName: '',
    selectedAddressInfo: {} as DavaAddressInfo,
    email: '',
    phoneNumber: '',
    extraUserType: '',
    extraUserCvrNumber: '',
    extraUserCompanyName: '',
    extraUserCprNumber: '',
    extraUserFirstname: '',
    extraUserLastname: '',
    extraUserSelectedAddressInfo: {} as DavaAddressInfo,
  };

  errorInFirstname: boolean = false;
  errorInLastname: boolean = false;
  errorInCompanyName: boolean = false;
  errorInAddress: boolean = false;
  errorInEmail: boolean = false;
  errorInPhoneNumber: boolean = false;
  errorInExtraUserType: boolean = false; // error if extraUserType= ''
  errorInExtraUserLastname: boolean = false;
  errorInExtraUserFirstname: boolean = false;
  errorInExtraUserAddress: boolean = false;

  showTooltip_privateExtraUser: boolean = false;
  showTooltip_companyExtraUser: boolean = false;
  showTooltip_noExtraUser: boolean = false;

  cprDay: number = 0;
  cprMonth: number = 0;
  cprYear: number = 0;

  addressData: AddressData | undefined = undefined;
  extraUserAddressData: AddressData | undefined = undefined;
  addressSearchText: string = '';
  extraUserAddressSearchText: string = '';

  constructor() {}

  ngOnInit() {}

  recalculateHelvetiaPrices() {
    this.setCustomerInfoEvent.emit(this.helvetiaCustomerInfo);
    this.recalculateHelvetiaPricesEvent.emit();
  }

  toggleShowAsBusinessCustomer(showAsBusinessCustomer: boolean) {
    // If the state hasn't changed, exit to avoid unnecessary call
    if (
      this.helvetiaCustomerInfo.showAsBusinessCustomer ===
      showAsBusinessCustomer
    ) {
      return;
    }

    // Reset error flags related to customer information.
    this.resetCustomerInfoErrorFlags();

    // Update the 'showAsBusinessCustomer' state.
    this.helvetiaCustomerInfo.showAsBusinessCustomer = showAsBusinessCustomer;

    // Clear the 'extraUserCprNumber' field since to make customer enter it again
    this.helvetiaCustomerInfo.extraUserCprNumber = '';

    // Emit the updated customer information
    this.setCustomerInfoEvent.emit(this.helvetiaCustomerInfo);

    // Reset the 'extraUserAge' field, which is used for validation
    this.extraUserAge = 0;

    if (!showAsBusinessCustomer) {
      // If switching to 'private' customer type and a valid CPR number is provided:
      if (this.isValidCpr(this.helvetiaCustomerInfo.cprNumber)) {
        // Calculate the age based on the CPR number and update the 'extraUserAge'.
        const age = this.getAgeBasedOnCpr();
        this.extraUserAge = age;
        this.newAgeBasedOnCprEvent.emit(age);
      }
    }
    this.recalculateHelvetiaPricesEvent.emit();
  }

  lookupCPR() {
    if (this.helvetiaCustomerInfo.cprNumber.trim().length == 0) {
      this.errorInCprNumber = true;
      return;
    }
    this.cprLookupEvent.emit();
  }

  lookupCVR() {
    if (this.helvetiaCustomerInfo.cvrNumber.trim().length == 0) {
      this.errorInCvrNumber = true;
      return;
    }
    this.cvrLookupEvent.emit();
  }

  lookupExtraUserCPR() {
    if (this.helvetiaCustomerInfo.extraUserCprNumber.trim().length == 0) {
      return;
    }
    this.cprExtraUserLookupEvent.emit();
  }

  lookupExtraUserCVR() {
    if (this.helvetiaCustomerInfo.extraUserCvrNumber.trim().length == 0) {
      return;
    }

    this.cvrExtraUserLookupEvent.emit();
  }

  getAgeBasedOnCpr() {
    const currentDate = new Date();

    const yearSubstring = parseInt(
      currentDate.getFullYear().toString().substring(2),
    );
    const fullYear =
      this.cprYear < yearSubstring ? this.cprYear + 2000 : this.cprYear + 1900;

    const cprDate = new Date(fullYear, this.cprMonth - 1, this.cprDay);

    const year1 = cprDate.getFullYear();
    const month1 = cprDate.getMonth();
    const day1 = cprDate.getDate();

    const year2 = currentDate.getFullYear();
    const month2 = currentDate.getMonth();
    const day2 = currentDate.getDate();

    let yearDifference = year2 - year1;

    if (month2 < month1 || (month2 === month1 && day2 < day1)) {
      yearDifference--;
    }

    return yearDifference;
  }

  isValidCpr(cpr: string): boolean {
    // Check if the CPR number is exactly 10
    if (!/^\d{10}$/.test(cpr)) {
      return false;
    }

    // Extract the date part from the CPR number
    this.cprDay = parseInt(cpr.substring(0, 2), 10);
    this.cprMonth = parseInt(cpr.substring(2, 4), 10);
    this.cprYear = parseInt(cpr.substring(4, 6), 10);

    if (
      this.cprDay < 1 ||
      this.cprDay > 31 ||
      this.cprMonth < 1 ||
      this.cprMonth > 12
    ) {
      return false;
    }

    // Check last 4 digits
    const sequenceNumber = cpr.substring(6);
    if (!/^\d{4}$/.test(sequenceNumber)) {
      return false;
    }

    const age = this.getAgeBasedOnCpr();
    if (age < 18 || age > 120) {
      return false;
    }

    return true;
  }

  // This is used here and in Sjf so move to utility
  recalculateOnValidCpr() {
    const isValidCpr = this.isValidCpr(this.helvetiaCustomerInfo.cprNumber);
    this.errorInCprNumber = !isValidCpr;
    if (isValidCpr) {
      const age = this.getAgeBasedOnCpr();
      this.newAgeBasedOnCprEvent.emit(age);
      this.recalculateHelvetiaPrices();
    }
  }

  extraUserAge: number = 0;

  recalculateOnValidCprExtraUser() {
    const cpr = this.helvetiaCustomerInfo.extraUserCprNumber;
    const isValidCpr = this.isValidCpr(cpr);
    this.errorInExtraUserCprNumber = !isValidCpr;

    const age = this.getAgeBasedOnCpr();
    if (isValidCpr) {
      this.extraUserAge = age;
      this.newAgeBasedOnExtraUserCprEvent.emit(age);

      if (
        isValidCpr &&
        this.helvetiaCustomerInfo.showAsBusinessCustomer &&
        this.helvetiaCustomerInfo.extraUserType == 'private'
      ) {
        this.newAgeBasedOnCprEvent.emit(age);
      }
      this.recalculateHelvetiaPrices();
    }

    // Reset the values you you ented a vaild cpr for the extra user, and delete it again
    if (this.extraUserAge != 0 && cpr.length == 0) {
      this.extraUserAge = 0;
      this.newAgeBasedOnExtraUserCprEvent.emit(0);
      this.recalculateHelvetiaPrices();
    }
  }

  setAddress(address: AddressData) {
    if (!address) return;
    this.addressData = address;

    const customerStreet = !this.seachManuelAddress
      ? (address?.vejnavn ?? '')
      : this.manuelAddress.customerStreet;

    const customerBuildingNumber = !this.seachManuelAddress
      ? (address?.husnr ?? '')
      : this.manuelAddress.customerBuildingNumber;

    const customerFloor = !this.seachManuelAddress
      ? null
      : this.manuelAddress.customerFloor;

    const customerDoor = !this.seachManuelAddress
      ? null
      : this.manuelAddress.customerDoor;

    const customerAdditionalCity = !this.seachManuelAddress
      ? null
      : this.manuelAddress.customerAdditionalCity;

    const customerCity = !this.seachManuelAddress
      ? (address?.postnrnavn ?? '')
      : this.manuelAddress.customerCity;

    const customerPostCode = !this.seachManuelAddress
      ? (address?.postnr ?? '')
      : this.manuelAddress.customerPostCode;

    const davaAddressInfo: DavaAddressInfo = {
      tekst: '',
      adresse: {
        id: '',
        status: 0,
        darstatus: 0,
        vejkode: '',
        vejnavn: customerStreet,
        adresseringsvejnavn: '',
        husnr: customerBuildingNumber,
        etage: customerFloor,
        dør: customerDoor,
        supplerendebynavn: customerAdditionalCity,
        postnr: customerPostCode,
        postnrnavn: customerCity,
        stormodtagerpostnr: null,
        stormodtagerpostnrnavn: null,
        kommunekode: '',
        adgangsadresseid: '',
        x: 0,
        y: 0,
        href: '',
      },
    };

    this.helvetiaCustomerInfo.selectedAddressInfo = davaAddressInfo;
  }

  // ######################################

  seachManuelAddress: boolean = false;

  manuelAddress: {
    customerStreet: string;
    customerBuildingNumber: string;
    customerFloor: string | null;
    customerDoor: string | null;
    customerAdditionalCity: string | null;
    customerCity: string;
    customerPostCode: string;
  } = {
    customerStreet: '',
    customerBuildingNumber: '',
    customerFloor: null,
    customerDoor: null,
    customerAdditionalCity: null,
    customerCity: '',
    customerPostCode: '',
  };

  errorInCustomerStreet: boolean = false;
  errorInCustomerBuildingNumber: boolean = false;
  errorInCustomerFloor: boolean = false;
  errorInCustomerDoor: boolean = false;
  errorInCustomerAdditionalCity: boolean = false;
  errorInCustomerCity: boolean = false;
  errorInCustomerPostCode: boolean = false;

  // ######################################

  clearManualAddressError(field: string): void {
    switch (field) {
      case 'customerStreet':
        this.errorInCustomerStreet = false;
        break;
      case 'customerBuildingNumber':
        this.errorInCustomerBuildingNumber = false;
        break;
      case 'customerFloor':
        this.errorInCustomerFloor = false;
        break;
      case 'customerDoor':
        this.errorInCustomerDoor = false;
        break;
      case 'customerAdditionalCity':
        this.errorInCustomerAdditionalCity = false;
        break;
      case 'customerCity':
        this.errorInCustomerCity = false;
        break;
      case 'customerPostCode':
        this.errorInCustomerPostCode = false;
        break;
      default:
        break;
    }
  }

  // ######################################

  seachManuelExtraAddress: boolean = false;

  manuelExtraAddress: {
    customerStreet: string;
    customerBuildingNumber: string;
    customerFloor: string | null;
    customerDoor: string | null;
    customerAdditionalCity: string | null;
    customerCity: string;
    customerPostCode: string;
  } = {
    customerStreet: '',
    customerBuildingNumber: '',
    customerFloor: null,
    customerDoor: null,
    customerAdditionalCity: null,
    customerCity: '',
    customerPostCode: '',
  };

  errorInExtraCustomerStreet: boolean = false;
  errorInExtraCustomerBuildingNumber: boolean = false;
  errorInExtraCustomerFloor: boolean = false;
  errorInExtraCustomerDoor: boolean = false;
  errorInExtraCustomerAdditionalCity: boolean = false;
  errorInExtraCustomerCity: boolean = false;
  errorInExtraCustomerPostCode: boolean = false;

  // ######################################

  setExtraUserAddress(address: AddressData) {
    if (!address) return;
    this.extraUserAddressData = address;
    console.log('setExtraUserAddress: ', this.extraUserAddressData);
    const customerStreet = !this.seachManuelAddress
      ? (address?.vejnavn ?? '')
      : this.manuelAddress.customerStreet;

    const customerBuildingNumber = !this.seachManuelAddress
      ? (address?.husnr ?? '')
      : this.manuelAddress.customerBuildingNumber;

    const customerFloor = !this.seachManuelAddress
      ? null
      : this.manuelAddress.customerFloor;

    const customerDoor = !this.seachManuelAddress
      ? null
      : this.manuelAddress.customerDoor;

    const customerAdditionalCity = !this.seachManuelAddress
      ? null
      : this.manuelAddress.customerAdditionalCity;

    const customerCity = !this.seachManuelAddress
      ? (address?.postnrnavn ?? '')
      : this.manuelAddress.customerCity;

    const customerPostCode = !this.seachManuelAddress
      ? (address?.postnr ?? '')
      : this.manuelAddress.customerPostCode;

    const davaAddressInfo: DavaAddressInfo = {
      tekst: '',
      adresse: {
        id: '',
        status: 0,
        darstatus: 0,
        vejkode: '',
        vejnavn: customerStreet,
        adresseringsvejnavn: '',
        husnr: customerBuildingNumber,
        etage: customerFloor,
        dør: customerDoor,
        supplerendebynavn: customerAdditionalCity,
        postnr: customerPostCode,
        postnrnavn: customerCity,
        stormodtagerpostnr: null,
        stormodtagerpostnrnavn: null,
        kommunekode: '',
        adgangsadresseid: '',
        x: 0,
        y: 0,
        href: '',
      },
    };

    this.helvetiaCustomerInfo.extraUserSelectedAddressInfo = davaAddressInfo;

    this.recalculateHelvetiaPrices();
  }

  resetCustomerInfoErrorFlags(): void {
    this.errorInCprNumber = false;
    this.errorInCvrNumber = false;
    this.errorInFirstname = false;
    this.errorInLastname = false;
    this.errorInCompanyName = false;
    this.errorInAddress = false;
    this.errorInEmail = false;
    this.errorInPhoneNumber = false;
    this.errorInExtraUserType = false;
    this.errorInExtraUserFirstname = false;
    this.errorInExtraUserLastname = false;
    this.errorInExtraUserCprNumber = false;
    this.errorInExtraUserCvrNumber = false;
    this.errorInExtraUserAddress = false;
    this.errorInExtraUserCompanyName = false;

    this.errorInExtraCustomerStreet = false;
    this.errorInExtraCustomerBuildingNumber = false;
    this.errorInExtraCustomerFloor = false;
    this.errorInExtraCustomerDoor = false;
    this.errorInExtraCustomerAdditionalCity = false;
    this.errorInExtraCustomerCity = false;
    this.errorInExtraCustomerPostCode = false;

    this.errorInCustomerStreet = false;
    this.errorInCustomerBuildingNumber = false;
    this.errorInCustomerFloor = false;
    this.errorInCustomerDoor = false;
    this.errorInCustomerAdditionalCity = false;
    this.errorInCustomerCity = false;
    this.errorInCustomerPostCode = false;
  }

  validateCustomerInfo() {
    this.resetCustomerInfoErrorFlags();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const customerInfo = this.helvetiaCustomerInfo;

    const extraUserType = customerInfo.extraUserType;
    const showAsBusinessCustomer = customerInfo.showAsBusinessCustomer;
    const isCprValid = this.isValidCpr(customerInfo.cprNumber);
    const isBusinessCustomer = showAsBusinessCustomer;
    const isCvrNumberEmpty = !customerInfo.cvrNumber.trim();
    const isFirstnameEmpty = !customerInfo.firstname.trim();
    const isLastnameEmpty = !customerInfo.lastname.trim();
    const isCompanyNameEmpty = !customerInfo.companyName.trim();
    const isAddressInfoEmpty = !this.addressData;

    const isEmailEmpty = !customerInfo.email.trim();
    const isEmailInvalid = !emailRegex.test(customerInfo.email.trim());
    const isPhoneNumberEmpty = !customerInfo.phoneNumber;
    const isPhoneNumberInvalidLength =
      customerInfo.phoneNumber.toString().length !== 8;
    const isExtraUserTypeEmpty = !extraUserType;
    const isExtraUserCvrNumberEmpty = !customerInfo.extraUserCvrNumber.trim();
    const isExtraUserCompanyNameEmpty =
      !customerInfo.extraUserCompanyName.trim();
    const isExtraUserCprNumberEmpty = !customerInfo.extraUserCprNumber.trim();
    const isExtraUserFirstnameEmpty = !customerInfo.extraUserFirstname.trim();
    const isExtraUserLastnameEmpty = !customerInfo.extraUserLastname.trim();
    const isExtraUserAddressInfoEmpty = !this.extraUserAddressData;
    const isExtraUserCprNumberValid = this.isValidCpr(
      customerInfo.extraUserCprNumber.trim(),
    );
    const extraUserTypeIsNone = customerInfo.extraUserType == 'none';
    if (
      (!isCprValid && !isBusinessCustomer) ||
      (isCvrNumberEmpty && isBusinessCustomer) ||
      (isFirstnameEmpty && !isBusinessCustomer) ||
      (isLastnameEmpty && !isBusinessCustomer) ||
      (isCompanyNameEmpty && isBusinessCustomer) ||
      (isAddressInfoEmpty && !this.seachManuelAddress) ||
      isEmailEmpty ||
      isEmailInvalid ||
      isPhoneNumberEmpty ||
      isPhoneNumberInvalidLength ||
      (isExtraUserTypeEmpty && isBusinessCustomer) ||
      (extraUserType == 'business' &&
        isExtraUserCvrNumberEmpty &&
        isBusinessCustomer) ||
      (extraUserType == 'business' &&
        isExtraUserCompanyNameEmpty &&
        isBusinessCustomer) ||
      (extraUserType == 'private' &&
        isExtraUserCprNumberEmpty &&
        isBusinessCustomer) ||
      (extraUserType == 'private' &&
        isExtraUserFirstnameEmpty &&
        isBusinessCustomer) ||
      (extraUserType == 'private' &&
        isExtraUserLastnameEmpty &&
        isBusinessCustomer) ||
      (extraUserType == 'private' &&
        !isExtraUserCprNumberValid &&
        isBusinessCustomer) ||
      (this.helvetiaCustomerInfo.extraUserType != 'none' &&
        isExtraUserAddressInfoEmpty &&
        isBusinessCustomer &&
        !this.seachManuelExtraAddress) ||
      (!isBusinessCustomer &&
        !isExtraUserCprNumberEmpty &&
        !isExtraUserCprNumberValid) ||
      (!this.manuelAddress.customerStreet && this.seachManuelAddress) ||
      (!this.manuelAddress.customerBuildingNumber && this.seachManuelAddress) ||
      (!this.manuelAddress.customerCity && this.seachManuelAddress) ||
      (!this.manuelAddress.customerPostCode && this.seachManuelAddress) ||
      (!this.manuelExtraAddress.customerStreet &&
        this.seachManuelExtraAddress &&
        !extraUserTypeIsNone) ||
      (!this.manuelExtraAddress.customerBuildingNumber &&
        this.seachManuelExtraAddress &&
        !extraUserTypeIsNone) ||
      (!this.manuelExtraAddress.customerCity &&
        this.seachManuelExtraAddress &&
        !extraUserTypeIsNone) ||
      (!this.manuelExtraAddress.customerPostCode &&
        this.seachManuelExtraAddress &&
        !extraUserTypeIsNone)
    ) {
      // Set error flags based on the conditions
      this.errorInExtraUserType = isExtraUserTypeEmpty && isBusinessCustomer;

      this.errorInExtraUserCvrNumber =
        extraUserType === 'business' && isExtraUserCvrNumberEmpty;

      this.errorInExtraUserCompanyName =
        extraUserType === 'business' && isExtraUserCompanyNameEmpty;

      if (isBusinessCustomer) {
        this.errorInExtraUserCprNumber =
          (extraUserType === 'private' && isExtraUserCprNumberEmpty) ||
          (extraUserType === 'private' && !isExtraUserCprNumberValid);
      } else {
        this.errorInExtraUserCprNumber =
          !isBusinessCustomer &&
          this.helvetiaCustomerInfo.extraUserCprNumber.trim().length > 0 &&
          !isExtraUserCprNumberValid;
      }

      this.errorInExtraUserFirstname =
        extraUserType === 'private' &&
        isBusinessCustomer &&
        isExtraUserFirstnameEmpty;

      this.errorInExtraUserLastname =
        extraUserType === 'private' &&
        isBusinessCustomer &&
        isExtraUserLastnameEmpty;

      this.errorInExtraUserAddress =
        extraUserType !== 'none' &&
        isExtraUserAddressInfoEmpty &&
        isBusinessCustomer &&
        !this.seachManuelExtraAddress;

      this.errorInCprNumber = !isCprValid && !isBusinessCustomer;

      this.errorInCvrNumber = isCvrNumberEmpty && isBusinessCustomer;

      this.errorInFirstname = isFirstnameEmpty && !isBusinessCustomer;

      this.errorInLastname = isLastnameEmpty && !isBusinessCustomer;

      this.errorInCompanyName = isCompanyNameEmpty && isBusinessCustomer;

      this.errorInAddress = isAddressInfoEmpty && !this.seachManuelAddress;

      this.errorInEmail = isEmailEmpty || isEmailInvalid;

      this.errorInPhoneNumber =
        isPhoneNumberEmpty || isPhoneNumberInvalidLength;

      this.errorInCustomerStreet =
        !this.manuelAddress.customerStreet && this.seachManuelAddress;

      this.errorInCustomerBuildingNumber =
        !this.manuelAddress.customerBuildingNumber && this.seachManuelAddress;

      this.errorInCustomerCity =
        !this.manuelAddress.customerCity && this.seachManuelAddress;

      this.errorInCustomerPostCode =
        !this.manuelAddress.customerPostCode && this.seachManuelAddress;

      this.errorInExtraCustomerStreet =
        !this.manuelExtraAddress.customerStreet &&
        this.seachManuelExtraAddress &&
        !extraUserTypeIsNone;

      this.errorInExtraCustomerBuildingNumber =
        !this.manuelExtraAddress.customerBuildingNumber &&
        this.seachManuelExtraAddress &&
        !extraUserTypeIsNone;

      this.errorInExtraCustomerCity =
        !this.manuelExtraAddress.customerCity &&
        this.seachManuelExtraAddress &&
        !extraUserTypeIsNone;

      this.errorInExtraCustomerPostCode =
        !this.manuelExtraAddress.customerPostCode &&
        this.seachManuelExtraAddress &&
        !extraUserTypeIsNone;

      return;
    }

    this.helvetiaCustomerInfo = {
      showAsBusinessCustomer: showAsBusinessCustomer,
      cprNumber: this.helvetiaCustomerInfo.cprNumber.trim(),
      cvrNumber: this.helvetiaCustomerInfo.cvrNumber.trim(),
      firstname: this.helvetiaCustomerInfo.firstname.trim(),
      lastname: this.helvetiaCustomerInfo.lastname.trim(),
      companyName: this.helvetiaCustomerInfo.companyName.trim(),
      selectedAddressInfo: this.helvetiaCustomerInfo.selectedAddressInfo, // SET ADRESS
      email: this.helvetiaCustomerInfo.email.trim(),
      phoneNumber: this.helvetiaCustomerInfo.phoneNumber.toString(),
      extraUserType: this.helvetiaCustomerInfo.extraUserType,
      extraUserCvrNumber: this.helvetiaCustomerInfo.extraUserCvrNumber?.trim(),
      extraUserCompanyName:
        this.helvetiaCustomerInfo.extraUserCompanyName?.trim(),
      extraUserCprNumber: this.helvetiaCustomerInfo.extraUserCprNumber?.trim(),
      extraUserFirstname: this.helvetiaCustomerInfo.extraUserFirstname.trim(),
      extraUserLastname: this.helvetiaCustomerInfo.extraUserLastname.trim(),
      extraUserSelectedAddressInfo:
        this.helvetiaCustomerInfo.extraUserSelectedAddressInfo, // SET EXTRA  ADRESS
    };

    if (this.seachManuelAddress) {
      const davaAddressInfo: DavaAddressInfo = {
        tekst: '',
        adresse: {
          id: '',
          status: 0,
          darstatus: 0,
          vejkode: '',
          vejnavn: this.manuelAddress.customerStreet.trim(),
          adresseringsvejnavn: '',
          husnr: this.manuelAddress.customerBuildingNumber.trim(),
          etage: this.manuelAddress.customerFloor
            ? this.manuelAddress.customerFloor.trim()
            : null,
          dør: this.manuelAddress.customerDoor
            ? this.manuelAddress.customerDoor.trim()
            : null,
          supplerendebynavn: this.manuelAddress.customerAdditionalCity
            ? this.manuelAddress.customerAdditionalCity.trim()
            : null,
          postnr: this.manuelAddress.customerPostCode,
          postnrnavn: this.manuelAddress.customerCity.trim(),
          stormodtagerpostnr: null,
          stormodtagerpostnrnavn: null,
          kommunekode: '',
          adgangsadresseid: '',
          x: 0,
          y: 0,
          href: '',
        },
      };

      //selectedAddressInfo
      this.helvetiaCustomerInfo.selectedAddressInfo = davaAddressInfo;
    }

    if (
      this.seachManuelExtraAddress &&
      this.helvetiaCustomerInfo.extraUserType !== 'none'
    ) {
      const davaAddressInfo: DavaAddressInfo = {
        tekst: '',
        adresse: {
          id: '',
          status: 0,
          darstatus: 0,
          vejkode: '',
          vejnavn: this.manuelExtraAddress.customerStreet.trim(),
          adresseringsvejnavn: '',
          husnr: this.manuelExtraAddress.customerBuildingNumber.trim(),
          etage: this.manuelExtraAddress.customerFloor
            ? this.manuelExtraAddress.customerFloor.trim()
            : null,
          dør: this.manuelExtraAddress.customerDoor
            ? this.manuelExtraAddress.customerDoor.trim()
            : null,
          supplerendebynavn: this.manuelExtraAddress.customerAdditionalCity
            ? this.manuelExtraAddress.customerAdditionalCity.trim()
            : null,
          postnr: this.manuelExtraAddress.customerPostCode,
          postnrnavn: this.manuelExtraAddress.customerCity.trim(),
          stormodtagerpostnr: null,
          stormodtagerpostnrnavn: null,
          kommunekode: '',
          adgangsadresseid: '',
          x: 0,
          y: 0,
          href: '',
        },
      };

      //extraUserSelectedAddressInfo
      this.helvetiaCustomerInfo.extraUserSelectedAddressInfo = davaAddressInfo;
    }

    this.setCustomerInfoEvent.emit(this.helvetiaCustomerInfo);
    this.nextStepEvent.emit();
  }
}
